import {
  App,
  Button,
  Card,
  Descriptions,
  Divider,
  Dropdown,
  Flex,
  Form,
  Modal,
  notification,
  Space,
  Spin,
  Tooltip,
  Typography,
} from "antd";

import styles from "./task-templates-page.module.css";
import routes from "@/routes";

import Breadcrumbs from "@/components/ui/Breadcrumbs";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { UserContext } from "@/context/UserContext";

import {
  LOCAL_STORAGE_KEY,
  SORT_OPTIONS,
  NOT_FILLED_OPTION,
} from "@/constants/task-templates";

import {
  getList as getTemplates,
  deleteItem as deleteTemplate,
  updateItem as updateTemplate,
  createItem as createTemplate,
} from "@/api/task-templates";
import { getStatusOptions } from "@/api/statuses";
import { getImportanceOptions } from "@/api/importances";
import { getTaskTypeOptions } from "@/api/taskTypes";
import {
  getEmployeeUnitsOptions,
  getItem as getUnit,
  getUnitsOptions,
  getUnitsTree,
} from "@/api/units";
import { getEmployeesOptions, getUnitEmployees } from "@/api/employees";

import CustomSelect from "@/components/inputs/CustomSelect";
import CustomTreeSelect from "@/components/inputs/CustomTreeSelect";
import SearchFrom3 from "@/components/inputs/SearchFrom3";

import TaskTemplatesTable from "@/components/tables/TaskTemplatesTable";

import TaskTemplateForm from "@/components/forms/TaskTemplateForm";

import TaskTemplatesModal from "@/components/modals/TaskTemplateModal";
import FiltersModal from "@/components/modals/FiltersModal";

import FilterIcon from "@/icons/FilterIcon";
import CloseIcon from "@/icons/CloseIcon";
import SortDescendingIcon from "@/icons/SortDescendingIcon";
import SortAscendingIcon from "@/icons/SortAscendingIcon";
import PlusIcon from "@/icons/PlusIcon";

const { Title } = Typography;

const resolveWithNotFilled = (value, list) => {
  if (!value) return null;

  if (value === "notFilled") {
    return {
      uuid: "notFilled",
      label: "Не заполнено",
      name: "Не заполнено",
    };
  }

  return list.find(({ uuid }) => uuid === value);
};

// Страница шаблонов задач
// Имеет возможность фильтрации и сортировки шаблонов задач
const TaskTemplates = ({ isAdmins = false }) => {
  const { unit_uuid } = useParams();
  const [searchParams] = useSearchParams();
  const { message } = App.useApp();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const [authorSelectOpen, setAuthorSelectOpen] = useState(false);
  const [executorSelectOpen, setExecutorSelectOpen] = useState(false);

  // Данные
  const [unitData, setUnitData] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [optionsPending, setOptionsPending] = useState(true);

  const initialTableParams = {
    select_view: "list",
    pagination: {
      current: 1,
      placement: ["bottomLeft"],
      showSizeChanger: true,
    },
    filters: {
      search: null,
      status_uuid: [],
      importance_uuid: null,
      task_type_uuid: null,
      author_uuid: null,
      responsible_executor_uuid: null,
      unit_uuid: unit_uuid ? unit_uuid : null,
      units_uuids: [],
      access_level: "all",
    },
    sort: SORT_OPTIONS[0].value,
  };

  const [tableParams, setTableParams] = useState(initialTableParams);

  // Списки для фильтров
  const [statuses, setStatuses] = useState([]);
  const [allPriorities, setAllPriorities] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [units, setUnits] = useState([]);
  const [unitsTree, setUnitsTree] = useState([]);
  const [employees, setEmployees] = useState([]);

  // const deletedStatusUuid = useMemo(() => {
  //   return statuses.find((status) => status.is_for_deletion)?.uuid;
  // }, [statuses]);

  // Возвращает не удаленные фильтры
  const getActualPriorities = useMemo(() => {
    return allPriorities.filter(({ deleted }) => !deleted);
    // const hasDeletedStatus =
    //   tableParams.filters.status_uuid?.includes(deletedStatusUuid);

    // return hasDeletedStatus || !tableParams.filters.status_uuid?.length
    //   ? allPriorities
    //   : allPriorities.filter(({ deleted }) => !deleted);
  }, [allPriorities]);

  // Возвращает плоский список РГ
  const getFlatUnits = useMemo(() => {
    const flat = [];

    function traverse(nodes) {
      nodes.forEach((node) => {
        flat.push(node);
        if (Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children);
        }
      });
    }

    if (!unitsTree) return [];

    traverse(unitsTree);
    return flat;
  }, [unitsTree]);

  // Окно создания/редактирования
  const [isModalShow, setIsModalShow] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const [form] = Form.useForm();

  const handleSaveCard = async (values) => {
    try {
      const saved = await createTemplate({
        ...values,
        name: values.name || values.template_title,
      });

      const tplNumber = saved?.template_code ?? "";
      const tplName = saved?.template_title ?? "";

      notification.open({
        message: (
          <div style={{ fontWeight: 500 }}>
            Создан шаблон: {tplNumber ? `№${tplNumber} - ` : ""}
            {tplName}
          </div>
        ),
        description: (
          <Button
            type="link"
            style={{
              background: "transparent",
              border: "1px solid #D5E2F7",
              color: "#0066a6",
              fontWeight: 500,
            }}
            onClick={() =>
              navigate(
                isAdmins
                  ? routes.administration.taskTemplateDetail(saved.uuid)
                  : routes.taskTemplateDetail(saved.uuid),
              )
            }
          >
            Перейти к шаблону
          </Button>
        ),
        icon: null,
        duration: 4,
        pauseOnHover: true,
        placement: "bottomRight",
        style: {
          border: "1px solid #B7EB8F",
          background: "#f6ffed",
          borderRadius: "8px",
          width: "500px",
        },
      });

      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 5,
      });
    }
  };

  // Окно с фильтрами
  const [isFiltersModalShow, setIsFiltersModalShow] = useState(false);
  const [tempValueFilters, setTempValueFilters] = useState(null);

  const didInit = useRef(false);

  //  initial fetch
  useEffect(() => {
    fetchOptions().then(() => (didInit.current = true));
    // eslint-disable-next-line
  }, [unit_uuid, isAdmins]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        handleViewChange("cards");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line
  }, []);

  const fetchOptions = async () => {
    let isFetch = false;
    setOptionsPending(true);

    try {
      const [statuses, priorities, taskTypes, { units }, unit, tree] =
        await Promise.all([
          getStatusOptions(),
          getImportanceOptions(),
          getTaskTypeOptions(),
          isAdmins ? getUnitsOptions() : getEmployeeUnitsOptions(),
          unit_uuid && getUnit(unit_uuid),
          unit_uuid
            ? getUnitsTree({ unit_uuid: unit_uuid, from_unit: true })
            : getUnitsTree({ from_unit: true }),
        ]);

      setStatuses(
        statuses.map((item) => {
          return { ...item, label: item.name };
        }),
      );
      setAllPriorities(
        priorities.map((item) => {
          return { ...item, label: item.name };
        }),
      );
      setTaskTypes(
        taskTypes.map((item) => {
          return { ...item, label: item.name };
        }),
      );
      setUnits(units);
      setUnitsTree(tree);
      setUnitData(unit);

      // Инициализация параметров из localStorage
      let localParams =
        JSON.parse(
          localStorage.getItem(
            `${user.user_uuid}_${LOCAL_STORAGE_KEY}_${unit_uuid || ""}_${
              isAdmins ? "admins" : ""
            }`,
          ),
        ) || initialTableParams;

      // FIXME: переписать потом под другие фильтры (более универсально)
      const paramsStatus = searchParams.get("status");
      const paramsExecutor = searchParams.get("executor");

      if (paramsStatus || paramsExecutor) {
        localParams = {
          ...initialTableParams,
          filters: {
            ...initialTableParams.filters,
            ...(paramsExecutor && {
              responsible_executor_uuid: paramsExecutor,
            }),
            ...(paramsStatus && { status_uuid: [paramsStatus] }),
          },
        };

        navigate(window.location.pathname, { replace: true });
      }

      setTableParams(localParams);

      // Fixme: Возможно нужно будет учитывать выбранные units_uuids в фильтрах, но пока не делаем
      let users = await fetchUserList();

      // Проверяем необходимость обновления фильтров
      const newFilters = { ...localParams.filters };

      // Если ни один статус не выбран, то установить статусы по умолчанию ( все, кроме удаленных)
      if (!newFilters.status_uuid) {
        newFilters.status_uuid = statuses
          .filter((item) => !item.is_for_deletion && !item.is_for_cancellation)
          .map((item) => item.uuid);
      }

      // Если фильтр по рабочей группе, но ее нет в списке, сбросить фильтр
      if (
        newFilters.unit_uuid &&
        !units.some((unit) => unit.uuid === newFilters.unit_uuid)
      ) {
        newFilters.unit_uuid = null;
        users = await fetchUserList();
      }

      // Fixme: Дописать. Если в фильтре по дереву рабочих групп есть РГ, которых нет в дереве, сбросить фильтр

      // Если какой-то из статусов в фильтре выбран, но его нет в списке статусов, исключить его
      if (
        localParams.filters.status_uuid &&
        !localParams.filters.status_uuid.every((statusUuid) =>
          statuses.some((status) => status.uuid === statusUuid),
        )
      ) {
        newFilters.status_uuid = localParams.filters.status_uuid.filter(
          (statusUuid) => statuses.some((status) => status.uuid === statusUuid),
        );
      }

      // Если выбранный приоритет отсутствует в списке приоритетов, сбросить его
      if (
        localParams.filters.importance_uuid &&
        !priorities
          .filter(({ deleted }) => !deleted)
          .some(
            (priority) => priority.uuid === localParams.filters.importance_uuid,
          )
      ) {
        newFilters.importance_uuid = null;
      }
      // Если выбранный тип задачи отсутствует в списке типов задач, сбросить его
      if (
        localParams.filters.task_type_uuid &&
        !taskTypes
          .filter(({ deleted }) => !deleted)
          .some(
            (taskType) => taskType.uuid === localParams.filters.task_type_uuid,
          )
      ) {
        newFilters.task_type_uuid = null;
      }

      // Если выбранный автор отсутствует в списке сотрудников, сбросить его
      if (
        localParams.filters.author_uuid &&
        !users.some(
          (employee) => employee.uuid === localParams.filters.author_uuid,
        )
      ) {
        newFilters.author_uuid = null;
      }

      // Если выбранный исполнитель отсутствует в списке сотрудников, сбросить его
      if (
        localParams.filters.responsible_executor_uuid &&
        localParams.filters.responsible_executor_uuid !== "notFilled" &&
        !users.some(
          (employee) =>
            employee.uuid === localParams.filters.responsible_executor_uuid,
        )
      ) {
        newFilters.responsible_executor_uuid = null;
      }

      // Если фильтры изменились, обновляем их
      if (JSON.stringify(newFilters) !== JSON.stringify(localParams.filters)) {
        isFetch = true;
        updateFilters(localParams, newFilters, true);
      }

      if (!isFetch) {
        fetchData(localParams);
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  };

  // Запрос на сервер для получения данных
  const fetchData = async (tParams) => {
    setIsLoading(true);

    const currentParams = tParams || tableParams;
    const params = {
      page: currentParams.pagination.current,
      perpage: currentParams.pagination.pageSize,
      sort: currentParams.sort,
      ...currentParams.filters,
      type_filter: currentParams.filters.access_level,
    };

    // Дерево работает с объектами, поэтому для запроса нужно преобразовать их в массив uuids
    if (params.units_uuids?.length) {
      params.units_uuids = params.units_uuids.map(({ value }) => value);
    }

    try {
      const { templates, total, page, perPage } = await getTemplates(
        params,
        isAdmins,
      );
      setData(templates);

      const currentParams = tParams || tableParams;
      const updatedTableParams = {
        ...currentParams,
        pagination: {
          ...currentParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setParams(updatedTableParams);
      form.resetFields();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Устанавливаем параметры таблицы и сохраняем их в localStorage
  const setParams = (params) => {
    setTableParams(params);

    // Сохраняем параметры пагинации в localStorage
    localStorage.setItem(
      `${user.user_uuid}_${LOCAL_STORAGE_KEY}_${unit_uuid || ""}_${
        isAdmins ? "admins" : ""
      }`,
      JSON.stringify(params),
    );
  };

  const updateFilters = (localParams, newFilters, resetPage = false) => {
    const updatedFilters = { ...localParams.filters, ...newFilters };

    const updatedTableParams = {
      ...localParams,
      filters: updatedFilters,
      pagination: {
        ...localParams.pagination,
        current: resetPage ? 1 : localParams.pagination.current,
      },
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  const handleViewChange = (value) => {
    const updatedTableParams = {
      ...tableParams,
      select_view: value,
    };

    setParams(updatedTableParams);
  };

  // Обработчик для смены фильтров
  const handleFilterChange = async (name, val) => {
    updateFilters(
      tableParams,
      {
        [name]: val || null,
        ...(name === "units_uuids" && {
          access_level: "all",
        }),
        ...(name === "access_level" && {
          units_uuids:
            val === "all"
              ? []
              : val === "root"
                ? getFlatUnits.filter(({ readOnly }) => !readOnly)
                : getFlatUnits.filter(({ readOnly }) => readOnly),
        }),
        // ...(name === "status_uuid" && {
        //   importance_uuid: null,
        // }),
      },
      true,
    );

  };

  // Обработчик для открытия модалки с фильтрами
  const openFiltersModal = () => {
    setTempValueFilters({
      ...tableParams.filters,
    });
    setIsFiltersModalShow(true);
  };

  // Обработчик для смены временных фильтров в модалке
  const handleTempFilterChange = (name, val) => {
    setTempValueFilters((prev) => ({
      ...prev,
      [name]: val || null,
      ...(name === "units_uuids" && {
        access_level: "all",
      }),
      ...(name === "access_level" && {
        units_uuids:
          val === "all"
            ? []
            : val === "root"
              ? getFlatUnits.filter(({ readOnly }) => !readOnly)
              : getFlatUnits.filter(({ readOnly }) => readOnly),
      }),

      // ...(name === "status_uuid" && {
      //   importance_uuid: null,
      // }),
    }));
  };

  const handleSortChange = (val) => {
    const updatedTableParams = {
      ...tableParams,
      sort: val,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    };
    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Получение списка пользователей для фильтров
  const fetchUserList = async (unit_uuid) => {
    try {
      let list = [];
      if (isAdmins && !unit_uuid) {
        const { employees } = await getEmployeesOptions({
          is_dismissed: false,
        });
        list = employees.map(({ employee }) => employee);
      } else {
        const { members } = await getUnitEmployees({
          ...(unit_uuid && {
            unit_uuid: unit_uuid,
          }),
        });
        list = members;
      }

      const res = list.map((item) => {
        const { lastname, firstname, patronymic } = item;
        return {
          ...item,
          label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`,
        };
      });

      setEmployees(res);
      return res;
    } catch (error) {
      console.log(error);
    }
  };

  // Применение выбранных фильтров из модалки
  const applyFilters = () => {
    updateFilters(
      tableParams,
      {
        ...tempValueFilters,
      },
      true,
    );
    setTempValueFilters(null);
    setIsFiltersModalShow(false);
  };

  // Очистка всех фильтров
  const clearFilters = () => {
    const updatedTableParams = {
      ...tableParams,
      filters: { ...initialTableParams.filters },
      pagination: {
        ...tableParams.pagination,
        current: 1, // Сбрасываем на первую страницу
      },
    };

    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Обработчик изменения пагинации или сортировки, которые приходят из таблицы
  const handleTableChange = (pagination, filters, sorter) => {
    // if (sorter) {
    //   const sortArray = Array.isArray(sorter) ? sorter : [sorter];
    //   const sortString = sortArray
    //     .map(({ field, order }) => {
    //       return order === "descend" ? `-${field}` : field;
    //     })
    //     .join(",");

    //   params.sort = sortString;
    // }
    const updatedTableParams = {
      ...tableParams,

      pagination: {
        ...tableParams.pagination,
        current: pagination.current || tableParams.pagination.current,
        pageSize: pagination.pageSize || tableParams.pagination.pageSize,
      },
    };
    setParams(updatedTableParams);
    fetchData(updatedTableParams);
  };

  // Обработчик добавления. Открывает модальное окно
  const handleAddNew = () => {
    if (tableParams.filters.unit_uuid) {
      setCurrentItem({
        unit_uuid: tableParams.filters.unit_uuid,
      });
    } else {
      setCurrentItem(null);
    }
    setIsModalShow(true);
  };

  // Обработчик редактирования. Открывает модальное окно
  const handleEditItem = (item) => {
    setCurrentItem(item);
    setIsModalShow(true);
  };

  // Обработчик сохранения. Отправляет запрос на сервер и обновляет данные
  const handleSaveItem = async () => {
    setIsModalShow(false);
    await fetchData();
  };

  // Обработчик удаления
  const handleDeleteItem = (item) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить шаблон ${item.template_code} - ${item.template_title}?`,
      content: "Это действие нельзя будет отменить.",
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем шаблон задачи после подтверждения
  const handleConfirmDelete = async (item) => {
    setIsLoading(true);
    try {
      await deleteTemplate({ uuid: item.uuid });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  // Обработчик видимости. Отправляет запрос на сервер и обновляет данные
  const handleUpdateVisibleItem = async (item, value) => {
    setIsLoading(true);
    try {
      await updateTemplate({ uuid: item.uuid, is_visible: value });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  const isAnyFilterApplied = useMemo(() => {
    const { filters } = tableParams;

    return Object.entries(filters).some(([key, value]) => {
      const initialValue = initialTableParams.filters[key];
      if (!initialValue && !value) return false;

      if (Array.isArray(value)) {
        return JSON.stringify(value) !== JSON.stringify(initialValue);
      }

      return value !== initialValue;
    });
    // eslint-disable-next-line
  }, [tableParams]);

  // Формируем массив выбранных фильтров для отдельной плашки
  const selectedFilters = useMemo(() => {
    const {
      status_uuid,
      importance_uuid,
      task_type_uuid,
      author_uuid,
      responsible_executor_uuid,
    } = tableParams.filters;

    const statusesList = (status_uuid || [])
      .map((id) => statuses.find(({ uuid }) => uuid === id))
      .filter(Boolean);

    const importance = allPriorities.find(
      ({ uuid }) => uuid === importance_uuid,
    );
    const taskType = taskTypes.find(({ uuid }) => uuid === task_type_uuid);
    const author = employees.find(({ uuid }) => uuid === author_uuid);

    const responsibleExecutor = resolveWithNotFilled(
      responsible_executor_uuid,
      employees,
    );

    return [
      ...statusesList.map((s) => {
        return {
          ...s,
          label: `Статус: ${s.label}`,
          value: `status_uuid__${s.uuid}`,
        };
      }),
      importance && {
        ...importance,
        label: `Приоритет: ${importance.label}`,
        value: `importance_uuid__${importance.uuid}`,
      },
      taskType && {
        ...taskType,
        label: `Тип задачи: ${taskType.label}`,
        value: `task_type_uuid__${taskType.uuid}`,
      },
      author && {
        ...author,
        label: `Автор: ${author.label}`,
        value: `author_uuid__${author.uuid}`,
      },
      responsibleExecutor && {
        ...responsibleExecutor,
        label: `Исполнитель: ${responsibleExecutor.label}`,
        value: `responsible_executor_uuid__${responsibleExecutor.uuid}`,
      },
    ].filter(Boolean);
  }, [tableParams, statuses, allPriorities, employees, taskTypes]);

  const handleDeselectFilter = (value, option) => {
    const [name, id] = value.split("__");
    if (name === "status_uuid") {
      handleFilterChange(
        name,
        tableParams.filters.status_uuid.filter((uuid) => uuid !== id),
      );
    } else {
      handleFilterChange(name, null);
    }
  };
  return (
    <>
      {optionsPending ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Flex vertical gap={25}>
            {/* Хлебные крошки и заголовок */}
            <Flex vertical gap={10}>
              <Breadcrumbs
                items={
                  isAdmins
                    ? [
                        { title: "Административная панель" },

                        {
                          title: "Задачи",
                        },
                        {
                          title: "Шаблоны задач",
                        },
                      ]
                    : [
                        {
                          title: "Главная",
                          href: routes.dashboards,
                        },

                        unitData?.name
                          ? {
                              title: unitData.name,
                            }
                          : {},
                        {
                          title: "Задачи",
                        },
                        {
                          title: "Шаблоны задач",
                        },
                      ]
                }
              />

              <Flex align="center" justify="space-between" gap={20}>
                <Title level={2} className="no-margin">
                  Шаблоны задач
                </Title>

                <Button
                  type="primary"
                  icon={<PlusIcon />}
                  onClick={handleAddNew}
                ></Button>
              </Flex>
            </Flex>

            {/* Поиск и фильтры */}
            <Flex vertical gap={8} className={styles.filtersWrap}>
              <Flex gap={25} align="start" justify="space-between">
                <Flex gap={8} align="center" className={styles.search}>
                  <SearchFrom3
                    placeholder="Поиск"
                    defaultValue={tableParams.filters.search}
                    onSearch={(search) => handleFilterChange("search", search)}
                  />

                  <Flex gap={8} className={styles.filters}>
                    {isAdmins ? (
                      <CustomSelect
                        placeholder="Рабочая группа"
                        options={units}
                        fieldNames={{
                          label: "name",
                          value: "uuid",
                          key: "uuid",
                        }}
                        style={{
                          width: 150,
                        }}
                        showSearch
                        optionFilterProp="name"
                        allowClear
                        popupMatchSelectWidth={false}
                        value={tableParams.filters.unit_uuid}
                        onChange={(unit) =>
                          handleFilterChange("unit_uuid", unit)
                        }
                      />
                    ) : (
                      <>
                        <CustomTreeSelect
                          placeholder="Рабочая группа"
                          data={unitsTree}
                          value={tableParams.filters.units_uuids}
                          onChange={(units_uuids) => {
                            handleFilterChange("units_uuids", units_uuids);
                          }}
                          style={{
                            width: "180px",
                          }}
                        />
                      </>
                    )}
                  </Flex>
                  <Button
                    type="primary"
                    icon={<FilterIcon />}
                    onClick={openFiltersModal}
                  ></Button>

                  {isAnyFilterApplied && (
                    <Tooltip
                      title="Очистить все фильтры"
                      placement="bottom"
                      className={styles.cleanFilters}
                    >
                      <Button
                        color="default"
                        variant="link"
                        shape="circle"
                        size="small"
                        icon={<CloseIcon />}
                        onClick={clearFilters}
                        style={{ background: "#BBBBBB", color: "#fff" }}
                      ></Button>
                    </Tooltip>
                  )}
                </Flex>

                <Flex gap={8} align="center">
                  <CustomSelect
                    placeholder="Сортировка"
                    options={SORT_OPTIONS}
                    style={{
                      width: 165,
                    }}
                    popupMatchSelectWidth={false}
                    value={tableParams.sort}
                    onChange={handleSortChange}
                    className={styles.bigSorting}
                  />

                  <div className={styles.smallSorting}>
                    <Dropdown
                      menu={{
                        items: SORT_OPTIONS,
                        selectable: true,
                        selectedKeys: tableParams.sort,
                        onClick: (e) => {
                          handleSortChange(e.key);
                        },
                      }}
                      trigger={["click"]}
                    >
                      <Button
                        icon={
                          tableParams.sort?.startsWith("-") ? (
                            <SortDescendingIcon style={{ fontSize: 16 }} />
                          ) : (
                            <SortAscendingIcon style={{ fontSize: 16 }} />
                          )
                        }
                      />
                    </Dropdown>
                  </div>
                </Flex>
              </Flex>

              {!!selectedFilters.length && (
                <CustomSelect
                  mode="multiple"
                  className={`${styles.selectedFilters} select__not-active`}
                  value={selectedFilters.map((f) => f.value)}
                  options={selectedFilters}
                  maxTagCount="responsive"
                  popupRender={() => null}
                  open={false}
                  suffixIcon={false}
                  showSearch={false}
                  onDeselect={handleDeselectFilter}
                  style={{ width: "100%" }}
                />
              )}
            </Flex>

            {/* Контент */}
            <Flex gap={20} className={styles.contentWrap}>
              <div className={styles.tableWrap}>
                <TaskTemplatesTable
                  data={data}
                  loading={isLoading}
                  pagination={tableParams.pagination}
                  onChange={handleTableChange}
                  onEditItem={handleEditItem}
                  onDeleteItem={handleDeleteItem}
                  onUpdateVisibleItem={handleUpdateVisibleItem}
                  unit={unit_uuid}
                  isAdmins={isAdmins}
                  scroll={{ x: "max-content" }}
                  resize
                />
              </div>
              <Card className={styles.formCard}>
                <TaskTemplateForm
                  isAdmins={isAdmins}
                  form={form}
                  data={
                    tableParams.filters.unit_uuid
                      ? { unit_uuid: tableParams.filters.unit_uuid }
                      : null
                  }
                  statuses={statuses}
                  priorities={allPriorities}
                  taskTypes={taskTypes}
                  units={units}
                  formSize="small"
                  showSubmit
                  onSubmit={async (values) => {
                    const payload = await form.validateFields();
                    await handleSaveCard(payload);
                  }}
                />
              </Card>
            </Flex>
          </Flex>

          {isModalShow && (
            <TaskTemplatesModal
              isAdmins={isAdmins}
              data={currentItem}
              statuses={statuses}
              priorities={allPriorities}
              taskTypes={taskTypes}
              units={units}
              visible={isModalShow}
              onSave={handleSaveItem}
              onCancel={() => setIsModalShow(false)}
              unit_uuid={unit_uuid}
            />
          )}

          {isFiltersModalShow && (
            <FiltersModal
              visible={isFiltersModalShow}
              onSave={applyFilters}
              onCancel={() => {
                setIsFiltersModalShow(false);
              }}
              onClear={() => {
                setIsFiltersModalShow(false);
                clearFilters();
              }}
            >
              <Descriptions
                column={1}
                colon={false}
                styles={{
                  label: {
                    display: "inline-block",
                    minWidth: "150px",
                    color: "#777777",
                  },
                  content: {
                    display: "block",
                    marginLeft: "8px",
                  },
                }}
              >
                {isAdmins ? (
                  <Descriptions.Item
                    label="Рабочая группа"
                    className={styles.modalFilter}
                  >
                    <CustomSelect
                      placeholder="Начните вводить"
                      options={units}
                      fieldNames={{
                        label: "name",
                        value: "uuid",
                        key: "uuid",
                      }}
                      style={{
                        width: "100%",
                      }}
                      showSearch
                      optionFilterProp="name"
                      allowClear
                      popupMatchSelectWidth={false}
                      defaultValue={tempValueFilters.unit_uuid}
                      onChange={(unit) =>
                        handleTempFilterChange("unit_uuid", unit)
                      }
                    />
                  </Descriptions.Item>
                ) : (
                  <>
                    <Descriptions.Item
                      label="Рабочая группа"
                      className={styles.modalFilter}
                    >
                      <CustomTreeSelect
                        placeholder="Начните вводить"
                        data={unitsTree}
                        value={tempValueFilters.units_uuids}
                        onChange={(units_uuids) => {
                          handleTempFilterChange("units_uuids", units_uuids);
                        }}
                        style={{
                          width: "100%",
                        }}
                      />
                    </Descriptions.Item>
                  </>
                )}

                <Descriptions.Item label="Приоритет">
                  <CustomSelect
                    placeholder="Начните вводить"
                    options={getActualPriorities}
                    fieldNames={{
                      label: "name",
                      value: "uuid",
                      key: "uuid",
                    }}
                    style={{
                      width: "100%",
                    }}
                    showSearch
                    optionFilterProp="name"
                    allowClear
                    popupMatchSelectWidth={false}
                    value={tempValueFilters.importance_uuid}
                    onChange={(val) =>
                      handleTempFilterChange("importance_uuid", val)
                    }
                  />
                </Descriptions.Item>

                <Descriptions.Item label="Тип задачи">
                  <CustomSelect
                    placeholder="Начните вводить"
                    options={taskTypes}
                    fieldNames={{
                      label: "name",
                      value: "uuid",
                      key: "uuid",
                    }}
                    style={{
                      width: "100%",
                    }}
                    showSearch
                    optionFilterProp="name"
                    allowClear
                    popupMatchSelectWidth={false}
                    value={tempValueFilters.task_type_uuid}
                    onChange={(val) =>
                      handleTempFilterChange("task_type_uuid", val)
                    }
                  />
                </Descriptions.Item>

                <Descriptions.Item label="Автор">
                  <CustomSelect
                    open={authorSelectOpen}
                    onOpenChange={setAuthorSelectOpen}
                    placeholder="Начните вводить"
                    value={tempValueFilters.author_uuid}
                    options={employees}
                    showSearch
                    allowClear
                    fieldNames={{ value: "uuid" }}
                    optionFilterProp="label"
                    style={{
                      width: "100%",
                    }}
                    placement="topRight"
                    popupMatchSelectWidth={false}
                    onChange={(val) =>
                      handleTempFilterChange("author_uuid", val)
                    }
                    popupRender={(menu) => (
                      <>
                        {menu}
                        {employees
                          .map((u) => u.uuid)
                          .includes(user.employee_uuid) && (
                          <>
                            <Divider
                              style={{
                                margin: "8px 0",
                              }}
                            />
                            <Space
                              style={{
                                padding: "0 8px 4px",
                              }}
                            >
                              <Typography.Link
                                onClick={() => {
                                  handleTempFilterChange(
                                    "author_uuid",
                                    user.employee_uuid,
                                  );

                                  setAuthorSelectOpen(false);
                                }}
                              >
                                Я автор
                              </Typography.Link>
                            </Space>
                          </>
                        )}
                      </>
                    )}
                  />
                </Descriptions.Item>

                <Descriptions.Item label="Исполнитель">
                  <CustomSelect
                    open={executorSelectOpen}
                    onOpenChange={setExecutorSelectOpen}
                    placeholder="Начните вводить"
                    value={tempValueFilters.responsible_executor_uuid}
                    options={[NOT_FILLED_OPTION, ...employees]}
                    showSearch
                    allowClear
                    fieldNames={{ value: "uuid" }}
                    optionFilterProp="label"
                    style={{
                      width: "100%",
                    }}
                    placement="topRight"
                    popupMatchSelectWidth={false}
                    onChange={(val) =>
                      handleTempFilterChange("responsible_executor_uuid", val)
                    }
                    popupRender={(menu) => (
                      <>
                        {menu}
                        {employees
                          .map((u) => u.uuid)
                          .includes(user.employee_uuid) && (
                          <>
                            <Divider
                              style={{
                                margin: "8px 0",
                              }}
                            />
                            <Space
                              style={{
                                padding: "0 8px 4px",
                              }}
                            >
                              <Typography.Link
                                onClick={() => {
                                  handleTempFilterChange(
                                    "responsible_executor_uuid",
                                    user.employee_uuid,
                                  );
                                  setExecutorSelectOpen(false);
                                }}
                              >
                                Я исполнитель
                              </Typography.Link>
                            </Space>
                          </>
                        )}
                      </>
                    )}
                  />
                </Descriptions.Item>
              </Descriptions>
            </FiltersModal>
          )}
        </>
      )}
    </>
  );
};

export default TaskTemplates;
